import { colorsPalettes, mixins, rgba } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { ChipDownContainer } from "@similarweb/ui-components";
import { PlainTooltip } from "@similarweb/ui-components";
import { SearchInput } from "@similarweb/ui-components";
import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import { CategoryItem } from "app/components/React/CategoriesDropdown/CategoryDropdown";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { WordsCloud } from "pro-features/components/TagsCloud/WordsCloud";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    bigNumberFilter,
    categoryClassIconFilter,
    i18nCategoryFilter,
    minVisitsFilter,
    percentageFilter,
    suffixFilter,
} from "app/filters/ngFilters";
import { SingleBox } from "app/pages/website-analysis/audience-interests/single/AudienceInterestsTableTop/AudienceInterestsTableTopStyles";
import { CategoryDistribution } from "app/pages/website-analysis/incoming-traffic/CategoryDisterbution";
import { DownloadExcelContainer, SearchContainer } from "app/pages/workspace/StyledComponent";
import type { FunctionComponent } from "react";
import { allTrackers } from "app/services/track/track";
import utils from "app/Shared/utils";
import styled, { css } from "styled-components";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { useCurrentStateAndParams } from "@uirouter/react";
import DurationService from "app/services/DurationService";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";

const Section = styled.div<{ margin?: number }>`
    padding: 0 12px;
    display: flex;
    align-items: center;
    ${({ margin }) =>
        margin &&
        css`
            margin: ${margin}px 0;
        `};
`;
const TotalItem = styled.div`
    height: 41px;
    flex-grow: 1;
    text-transform: capitalize;
    padding-left: 15px;
    display: flex;
    align-items: center;
    border-right: 1px solid ${colorsPalettes.carbon[50]};
    justify-content: center;
    &:last-of-type {
        border-right: 0;
    }
`;
const NumberStyled = styled.span`
    margin-right: 4px;
    ${mixins.setFont({ $size: 32, $color: colorsPalettes.carbon[500], $weight: 300 })};
`;
const TextStyled = styled.span`
    ${mixins.setFont({ $size: 14, $color: rgba(colorsPalettes.carbon[500], 0.8) })};
    max-width: 100px;
    margin-left: 8px;
    line-height: 19px;
`;
const Separator = styled.hr`
    border-top-color: ${colorsPalettes.carbon[50]};
    margin: 0;
`;
const CategoryItemContainer = styled(CategoryItem)`
    font-weight: 400;
`;
const TopStyled = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 24px;
`;
const TopStyledLeft = styled.div`
    display: flex;
    align-items: center;
`;
const ChipWrap = styled.div`
    padding-left: 8px;

    [data-automation-icon-name="arrow"] svg {
        width: 10px;
        height: 5px;
    }
`;
const StyledLabel = styled.div`
    ${mixins.setFont({ $color: colorsPalettes.carbon[500], $weight: 300, $size: 16 })};
`;

const Right = styled.div`
    flex-grow: 0;
    display: flex;
    align-items: center;
    margin-left: 10px;
`;
const CategoriesAndTopicsDist = styled.div`
    display: flex;
    padding: 17px;
`;
const CategoriesWrapper = styled(SingleBox)`
    flex-basis: 60%;
    margin-right: 16px;
`;

const TopicsWrapper = styled(SingleBox)`
    flex-basis: 40%;
    padding: 16px;
    box-sizing: border-box;
`;
const TopicsTitle = styled.div`
    ${mixins.setFont({ $size: 14, $color: colorsPalettes.carbon[500] })};
    display: flex;
`;

export const OutgoingTrafficPageTableTop: FunctionComponent<any> = (props) => {
    const { params } = useCurrentStateAndParams();
    const { webSource, isWWW, country, key, duration, selectedSite } = params;
    const durationObject = DurationService.getDurationData(duration);
    const { from, to } = durationObject.forAPI;
    const i18n = i18nFilter();
    const i18nCategory = i18nCategoryFilter();
    const bigNumber = bigNumberFilter();
    const minVisits = minVisitsFilter();
    const percentage = percentageFilter();
    const suffix = suffixFilter();
    const categoryClassIcon = categoryClassIconFilter();
    const {
        onSelectCategory: onSelectCategoryProps,
        tableTopData: {
            AllCategories,
            TotalVisits,
            TotalUnGroupedCount,
            TotalShare,
            domainMetaData,
            categoriesData,
            Topics,
        },
        isLoadingData,
        selectedCategory,
        selectedCategoryId,
        onClearAll,
        tableColumns,
        onClickToggleColumns,
        downloadExcelPermitted,
        excelLink,
        searchValue,
        onChange,
        a2d,
        getCategoryLink,
        onSelectSite,
        isCompare,
    } = props;
    const queryParamsForCompareTabs = {
        from,
        to,
        webSource,
        isWWW: isWWW !== "*",
        key,
        country,
    };
    const API = "/api/websiteanalysis/GetOutgoingDomainsCounts";
    const getCategoriesOptions = () => {
        const categories = utils.manipulateCategories(AllCategories);
        const items = categories.reduce((result, category) => {
            if (category.Sons.length > 0) {
                return [
                    ...result,
                    convertCategory(category),
                    ...category.Sons.map((son) => convertCategory(son, category.id)),
                ];
            } else {
                return [...result, convertCategory(category)];
            }
        }, []);

        return items.map((item, index) => {
            return (
                <CategoryItemContainer
                    {...item}
                    key={index}
                    selected={item.forApi === selectedCategoryId}
                />
            );
        });
    };
    const convertCategory = ({ Count, Name, Sons = [], id }, parentId = null) => {
        const text = `${i18nCategory(Name)}${Count ? ` (${Count})` : ``}`;
        return {
            text,
            id,
            isCustomCategory: false,
            isChild: Sons.length === 0,
            icon: categoryClassIcon(id),
            forApi: `${parentId ? `${parentId}~` : ``}${id}`,
        };
    };
    const onSelectCategory = (category) => {
        const value = category ? category.forApi.replace("~", " > ").replace("_", " ") : "Clear Filter";
        allTrackers.trackEvent("Drop Down", "click", `Table/Category/${value}`);
        onSelectCategoryProps(category && category.forApi);
    };
    const onClearCategory = () => {
        onSelectCategory(null);
    };
    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const onColumnToggle = (key) => {
        onClickToggleColumns(parseInt(key, 10));
    };
    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";
    const trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
    };
    return !isLoadingData ? (
        <>
            {isCompare ? (
                <CompareTabsContainer
                    onSelectSite={onSelectSite}
                    keyName="outgoingTrafficFirstLoad"
                    api={API}
                    domainQueryParamName={"selectedSite"}
                    queryParamsForCompareTabs={queryParamsForCompareTabs}
                />
            ) : null}
            <TopStyled>
                <TopStyledLeft>
                    <StyledLabel>{i18n("traffic.search.phrases.page.filterby.title")}</StyledLabel>
                    <ChipWrap>
                        <ChipDownContainer
                            width={340}
                            onClick={onSelectCategory}
                            selectedText={selectedCategory ?? ""}
                            onCloseItem={onClearCategory}
                            buttonText={i18n("common.category.all")}
                            searchPlaceHolder={i18n("home.dashboards.wizard.filters.searchCategory")}
                            noResultsMessage={i18n("dropdown.no.results")}
                            tooltipDisabled={true}
                            hasSearch={true}
                        >
                            {getCategoriesOptions()}
                        </ChipDownContainer>
                    </ChipWrap>
                </TopStyledLeft>
                <Button
                    type="flat"
                    onClick={onClearAll}
                >
                    {i18n("forms.buttons.clearall.text")}
                </Button>
            </TopStyled>
            <Separator />
            <Section margin={19}>
                <TotalItem>
                    <NumberStyled>{minVisits(bigNumber(TotalVisits))}</NumberStyled>
                    <TextStyled>{i18n("analysis.destination.out.visits")}</TextStyled>
                </TotalItem>
                <TotalItem>
                    <NumberStyled>{suffix(percentage(TotalShare, 2), "%")}</NumberStyled>
                    <TextStyled>{`${i18n("shared.totals.total")} ${i18n(
                        "analysis.destination.out.visits",
                    )}`}</TextStyled>
                </TotalItem>
                <TotalItem>
                    <NumberStyled>{TotalUnGroupedCount}</NumberStyled>
                    <TextStyled>{i18n("analysis.destination.out.domains")}</TextStyled>
                </TotalItem>
            </Section>
            <Separator />
            {domainMetaData ? (
                <CategoriesAndTopicsDist>
                    <CategoriesWrapper border={1}>
                        <CategoryDistribution
                            domains={domainMetaData}
                            data={categoriesData}
                            getLink={getCategoryLink}
                        />
                    </CategoriesWrapper>
                    <TopicsWrapper border={1}>
                        <TopicsTitle>
                            {i18n("shared.cats-topics.title2")}
                            <PlainTooltip
                                placement="top"
                                tooltipContent={i18n("shared.cats-topics.title2.tooltip")}
                            >
                                <span>
                                    <InfoIcon iconName="info" />
                                </span>
                            </PlainTooltip>
                        </TopicsTitle>
                        <WordsCloud tags={Topics} />
                    </TopicsWrapper>
                </CategoriesAndTopicsDist>
            ) : null}
            <SearchContainer>
                <SearchInput
                    disableClear={true}
                    defaultValue={searchValue}
                    debounce={400}
                    onChange={onChange}
                    placeholder={i18n("forms.search.placeholder")}
                />
                <Right>
                    <FlexRow>
                        <DownloadExcelContainer>
                            <DownloadButtonMenu
                                Excel={true}
                                downloadUrl={excelDownloadUrl}
                                exportFunction={trackExcelDownload}
                                excelLocked={!downloadExcelPermitted}
                            />
                        </DownloadExcelContainer>
                        <div>
                            <ColumnsPickerLite
                                {...getColumnsPickerLiteProps()}
                                translations={{
                                    tooltip: i18n("workspace.sales.column.picker"),
                                    getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                        i18n("common.columnsPicker.selectedAmount", {
                                            selected: selectedValue,
                                            total: totalValue,
                                        }),
                                    mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                    apply: i18n("common.apply"),
                                }}
                            />
                        </div>
                        <div>{a2d()}</div>
                    </FlexRow>
                </Right>
            </SearchContainer>
        </>
    ) : null;
};
